import { zodResolver } from '@hookform/resolvers/zod';
import { type Allocation } from 'components/new/AllocationsManager';
import { AllocationRule } from 'further-types/lp/allocations';
import { TransactionOperation } from 'further-types/lp/transaction';
import { CalendarDay } from 'further-ui/utils';
import { manualJournal as rules } from 'further-ui/validations';
import { round, sumBy } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

export type FormValues = z.infer<typeof rules.CreateManualAction>;

const emptyTransaction = {
  transactionTypeId: '',
  debitAmountLcy: 0,
  debitAmountTcy: 0,
  creditAmountLcy: 0,
  creditAmountTcy: 0,
};

const defaultValues = {
  description: '',
  ledgerDate: new CalendarDay(new Date()),
  allocations: [],
  transactions: [emptyTransaction, emptyTransaction],
  allocationRule: AllocationRule.ByCommitmentAndClosingDate,
  totalAmountTcy: 0,
  totalAmountLcy: 0,
};

export default function useManualActionForm({
  manualAction,
  handleTransactionSelection,
  reconcilableTransactions,
}: {
  manualAction?: FormValues;
  handleTransactionSelection: (selectedTransactionIds: Array<string>) => void;
  reconcilableTransactions: Array<{
    id: string;
    operationType: TransactionOperation;
    transactionTypeId: string;
    unreconciledAmountTcy: number;
  }>;
}) {
  const { control, watch, formState, setValue, handleSubmit } =
    useForm<FormValues>({
      shouldFocusError: true,
      resolver: zodResolver(rules.CreateManualAction),
      defaultValues,
      values: manualAction ?? defaultValues,
      criteriaMode: 'all',
      resetOptions: {
        keepDefaultValues: false,
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transactions',
  });

  const handleAddTransaction = () => {
    append(emptyTransaction);
  };

  const handleRemoveTransaction = (index: number) => {
    remove(index);
  };

  const handleAllocationRuleChange = (allocationRule: AllocationRule) => {
    setValue('allocationRule', allocationRule);
  };
  const handleAllocationsChange = (allocations: Array<Allocation>) => {
    setValue('allocations', allocations);
  };

  const formValues = watch();

  const setTotalAmounts = useCallback(() => {
    const totalAmountLcy = sumBy(
      formValues.transactions,
      (transaction) => transaction.debitAmountLcy,
    );
    const totalAmountTcy = sumBy(
      formValues.transactions,
      (transaction) => transaction.debitAmountTcy,
    );

    setValue('totalAmountLcy', round(totalAmountLcy, 2));
    setValue('totalAmountTcy', round(totalAmountTcy, 2));
  }, [formValues.transactions, setValue]);

  useEffect(() => {
    setTotalAmounts();
  }, [formValues.transactions, setTotalAmounts]);

  const handleTransactionTypeChange = (index: number, value: string) => {
    setValue(`transactions.${index}.transactionTypeId`, value);
  };

  const handleDebitAmountChange = (index: number, value: number) => {
    setValue(`transactions.${index}.debitAmountLcy`, value);
    setValue(`transactions.${index}.debitAmountTcy`, value);
    setValue(`transactions.${index}.creditAmountLcy`, 0);
    setValue(`transactions.${index}.creditAmountTcy`, 0);
    setTotalAmounts();
  };

  const handleCreditAmountChange = (index: number, value: number) => {
    setValue(`transactions.${index}.creditAmountLcy`, value);
    setValue(`transactions.${index}.creditAmountTcy`, value);
    setValue(`transactions.${index}.debitAmountLcy`, 0);
    setValue(`transactions.${index}.debitAmountTcy`, 0);
    setTotalAmounts();
  };

  const handleReconcilableTransactionSelection = (
    selectedTransactionIds: Array<string>,
  ) => {
    handleTransactionSelection(selectedTransactionIds);

    if (selectedTransactionIds.length === 0) {
      setValue('transactions', [emptyTransaction, emptyTransaction]);
      setValue('allocationRule', AllocationRule.ByCommitmentAndClosingDate);
      setValue('allocations', []);
      return;
    }

    const transactionsToKeep = formValues.transactions.filter(
      (transaction) =>
        !!transaction.reconcilableTransactionId &&
        selectedTransactionIds.includes(transaction.reconcilableTransactionId),
    );

    setValue('transactions', transactionsToKeep);
    setValue('allocationRule', AllocationRule.Reconciliation);
    setValue('allocations', []);

    selectedTransactionIds.forEach((transactionId) => {
      if (
        formValues.transactions.find(
          (transaction) =>
            transaction.reconcilableTransactionId === transactionId,
        )
      ) {
        return;
      }
      const reconcilableTransaction = reconcilableTransactions.find(
        (transaction) => transaction.id === transactionId,
      );
      if (!reconcilableTransaction) {
        return;
      }

      if (
        reconcilableTransaction.operationType === TransactionOperation.Credit
      ) {
        append([
          {
            reconcilableTransactionId: transactionId,
            transactionTypeId: reconcilableTransaction.transactionTypeId,
            debitAmountLcy: reconcilableTransaction.unreconciledAmountTcy,
            debitAmountTcy: reconcilableTransaction.unreconciledAmountTcy,
            creditAmountLcy: 0,
            creditAmountTcy: 0,
          },
          {
            reconcilableTransactionId: transactionId,
            transactionTypeId: '',
            debitAmountLcy: 0,
            debitAmountTcy: 0,
            creditAmountLcy: reconcilableTransaction.unreconciledAmountTcy,
            creditAmountTcy: reconcilableTransaction.unreconciledAmountTcy,
          },
        ]);
      } else {
        append([
          {
            reconcilableTransactionId: transactionId,
            transactionTypeId: reconcilableTransaction.transactionTypeId,
            debitAmountLcy: 0,
            debitAmountTcy: 0,
            creditAmountLcy: reconcilableTransaction.unreconciledAmountTcy,
            creditAmountTcy: reconcilableTransaction.unreconciledAmountTcy,
          },
          {
            reconcilableTransactionId: transactionId,
            transactionTypeId: '',
            debitAmountLcy: reconcilableTransaction.unreconciledAmountTcy,
            debitAmountTcy: reconcilableTransaction.unreconciledAmountTcy,
            creditAmountLcy: 0,
            creditAmountTcy: 0,
          },
        ]);
      }
    });
  };

  const transactionsError = formState.errors.transactions?.message;

  return {
    control,
    formValues,
    formState,
    transactionFields: fields,
    handleAddTransaction,
    handleRemoveTransaction,
    handleReconcilableTransactionSelection,
    handleAllocationRuleChange,
    handleAllocationsChange,
    handleTransactionTypeChange,
    handleDebitAmountChange,
    handleCreditAmountChange,
    transactionsError,
    handleSubmit,
  };
}
